import { useState } from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  TextField,
} from '@material-ui/core';

import { PRODUCT_SIZES, ProductSize } from '../../../products/types/product-sizes.type';
import { OrderItem } from '../../types/order-item.type';

const VIRTUAL_CATEGORY = 'hors-catalogue';

type VirtualToAdd = Omit<OrderItem, 'productId' | 'photo'>;

type Props = {
  onAdd: (item: VirtualToAdd) => void;
};

const useStyles = makeStyles((theme) => ({
  addVirtualProduct: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',

    '& > div': {
      display: 'flex',
      flexDirection: 'row',
      gap: '0.5rem',
    },
  },
  addButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

function parseProductSize(value: unknown): ProductSize {
  if (typeof value !== 'string') return PRODUCT_SIZES.M;
  switch (value) {
    case PRODUCT_SIZES.S:
      return PRODUCT_SIZES.S;
    case PRODUCT_SIZES.M:
      return PRODUCT_SIZES.M;
    case PRODUCT_SIZES.L:
      return PRODUCT_SIZES.L;
    case PRODUCT_SIZES.XL:
      return PRODUCT_SIZES.XL;
    case PRODUCT_SIZES.SOLO:
      return PRODUCT_SIZES.SOLO;
    default:
      return PRODUCT_SIZES.M;
  }
}

export function AddVirtualProductForm({ onAdd }: Props) {
  const classes = useStyles();

  const [name, setName] = useState('Produit hors catalogue');
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState(10);
  const [tvaRate, setTvaRate] = useState(5.5);
  const [size, setSize] = useState<ProductSize>(PRODUCT_SIZES.M);
  const [noLabel, setNoLabel] = useState(false);

  const handleAdd = () => {
    onAdd({
      name,
      category: VIRTUAL_CATEGORY,
      subcategory: VIRTUAL_CATEGORY,
      price,
      quantity,
      size,
      type: 'virtual',
      ref: 'Produit hors catalogue',
      tvaRate: tvaRate.toString(),
      noLabel,
      valueZero: false,
    });
    setName('Produit hors catalogue');
    setQuantity(1);
    setPrice(10);
  };

  return (
    <Paper className={classes.addVirtualProduct}>
      <TextField label="Nom" variant="outlined" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
      <div>
        <TextField
          label="Quantité"
          variant="outlined"
          fullWidth
          value={quantity}
          type="number"
          inputProps={{ min: 1 }}
          onChange={(e) => setQuantity(Number(e.target.value))}
        />
        <TextField
          label="Prix"
          variant="outlined"
          fullWidth
          value={price}
          type="number"
          inputProps={{ min: 0 }}
          InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
          onChange={(e) => setPrice(Number(e.target.value))}
        />
      </div>
      <div>
        <TextField
          label="Taux TVA"
          variant="outlined"
          fullWidth
          value={tvaRate}
          type="number"
          inputProps={{ min: 0 }}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          onChange={(e) => setTvaRate(Number(String(e.target.value).replace(',', '.')) || 0)}
        />
        <FormControl variant="outlined" style={{ minWidth: '110px' }}>
          <InputLabel>Taille</InputLabel>
          <Select
            type="string"
            autoWidth
            label="Taille"
            name="size"
            onChange={(e) => setSize(parseProductSize(e.target.value))}
            value={size}
          >
            {Object.values(PRODUCT_SIZES).map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <FormControlLabel
        style={{ margin: 0 }}
        control={<Checkbox checked={noLabel} onChange={() => setNoLabel((prev) => !prev)} name="no_label" />}
        label="Pas d'étiquette"
      />
      <div style={{ justifyContent: 'end' }}>
        <Button
          variant="text"
          onClick={() => {
            setName('Produit hors catalogue');
            setQuantity(1);
            setPrice(10);
          }}
        >
          Annuler
        </Button>
        <Button variant="contained" className={classes.addButton} onClick={handleAdd}>
          Ajouter
        </Button>
      </div>
    </Paper>
  );
}
